import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Paginator } from "primereact/paginator";
import { Toast } from "primereact/toast";
import { Dropdown } from "primereact/dropdown";
import { CopyToClipboard } from "react-copy-to-clipboard";
import config from "../../../config";

// Table component for displaying paper types
const PaperTypeTable = ({
  paperTypes,
  totalRecords,
  first,
  setFirst,
  rows,
  setRows,
  onPageChange,
  loading,
  selectedPaperType,
  handleEdit,
  handleDelete,
  syllabusOptions,
  selectedSyllabus,
  handleSyllabusChange,
  selectedMedium,
  handleMediumChange,
  selectedGrade,
  handleGradeChange,
  selectedSubject,
  handleSubjectChange,
  handleClearFilter,
}) => {
  const toast = useRef(null);
  const [mediumOptions, setMediumOptions] = useState([]);
  const [gradeOptions, setGradeOptions] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const navigate = useNavigate();

  const navigateToAddPaperType = () => {
    navigate("/add-paper-type");
  };

  const fetchMediums = async (syllabusId) => {
    try {
      const token = localStorage.getItem("token");
      const headersConfig = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await axios.get(
        `${config.apiUrl}/medium/bySyllabus?syllabusId=${syllabusId}`,
        headersConfig
      );
      const fetchedMediums = response.data.mediums;
      return fetchedMediums.map((medium) => ({
        label: medium.name,
        value: medium._id,
      }));
    } catch (error) {
      console.error("Error fetching mediums:", error);
      return [];
    }
  };

  useEffect(() => {
    if (selectedSyllabus) {
      const fetchData = async () => {
        const mediums = await fetchMediums(selectedSyllabus);
        setMediumOptions(mediums);
      };
      fetchData();
    }
  }, [selectedSyllabus]);

  const fetchGrades = async (syllabusId, mediumId) => {
    try {
      const token = localStorage.getItem("token");
      const headersConfig = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      let apiUrl = `${config.apiUrl}/grade/bySyllabusAndMedium`;
      const params = { syllabusId };

      if (mediumId) {
        params.mediumId = mediumId;
      }

      const response = await axios.get(apiUrl, {
        params,
        ...headersConfig,
      });

      const fetchedGrades = response.data.grades;
      return fetchedGrades.map((grade) => ({
        label: grade.name,
        value: grade._id,
      }));
    } catch (error) {
      console.error("Error fetching grades:", error);
    }
  };

  useEffect(() => {
    if (selectedSyllabus) {
      const fetchData = async () => {
        const grades = await fetchGrades(selectedSyllabus, selectedMedium);
        setGradeOptions(grades);
      };
      fetchData();
    }
  }, [selectedSyllabus, selectedMedium]);

  const fetchSubjects = async (syllabusId, mediumId) => {
    try {
      const token = localStorage.getItem("token");
      const headersConfig = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      let apiUrl = `${config.apiUrl}/subject/bySyllabusAndMedium`;
      const params = { syllabusId };

      if (mediumId) {
        params.mediumId = mediumId;
      }

      const response = await axios.get(apiUrl, {
        params,
        ...headersConfig,
      });

      const fetchedSubjects = response.data.subjects;
      return fetchedSubjects.map((subject) => ({
        label: subject.name,
        value: subject._id,
      }));
    } catch (error) {
      console.error("Error fetching subjects:", error);
    }
  };

  useEffect(() => {
    if (selectedSyllabus) {
      const fetchData = async () => {
        const subjects = await fetchSubjects(selectedSyllabus, selectedMedium);
        setSubjectOptions(subjects);
      };
      fetchData();
    }
  }, [selectedSyllabus, selectedMedium]);

  const renderVisibility = (rowData) => {
    return <span>{rowData.visibility ? "Yes" : "No"}</span>;
  };

  const renderImage = (rowData) => {
    if (rowData.image && typeof rowData.image === "string") {
      return (
        <img
          src={
            rowData.image.startsWith("http")
              ? rowData.image
              : `${config.apiUrl}/uploads/${rowData.image}`
          }
          alt={rowData.name}
          style={{ maxWidth: "100px", maxHeight: "100px" }}
        />
      );
    } else {
      return null;
    }
  };

  const handleCopyId = (id) => {
    toast.current.show({
      severity: "success",
      summary: "Success",
      detail: `ID copied successfully!`,
    });
  };

  const filteredPaperTypes = paperTypes.filter((paperType) => {
    const syllabusMatch =
      !selectedSyllabus ||
      (paperType.syllabus && paperType.syllabus._id === selectedSyllabus);
    const mediumMatch =
      !selectedMedium ||
      (paperType.medium && paperType.medium._id === selectedMedium);
    const gradeMatch =
      !selectedGrade ||
      (paperType.grade && paperType.grade._id === selectedGrade);
    const subjectMatch =
      !selectedSubject ||
      (paperType.subject && paperType.subject._id === selectedSubject);
    return syllabusMatch && mediumMatch && gradeMatch && subjectMatch;
  });

  return (
    <>
      <h3 style={{ float: "left", color: "#808080", marginTop: "20px" }}>
        Paper Types
      </h3>
      <Button
        label="+ New Paper Type"
        onClick={navigateToAddPaperType}
        style={{ float: "right", marginTop: "10px" }}
        rounded
        text
        raised
      />
      <br />
      <br />
      <br />
      <br />
      <div className="table-container">
        <Dropdown
          value={selectedSyllabus}
          options={syllabusOptions}
          onChange={handleSyllabusChange}
          placeholder="Select Syllabus"
          optionLabel="label"
          optionValue="value"
          showClear
          onClear={() => handleClearFilter("syllabus.name", "syllabusId")}
        />
        &nbsp;&nbsp;
        <Dropdown
          value={selectedMedium}
          options={mediumOptions}
          onChange={handleMediumChange}
          placeholder="Select Medium"
          optionLabel="label"
          optionValue="value"
          disabled={!selectedSyllabus}
          showClear
          onClear={() => handleClearFilter("medium.name", "mediumId")}
        />
        &nbsp;&nbsp;
        <Dropdown
          value={selectedGrade}
          options={gradeOptions}
          onChange={handleGradeChange}
          placeholder="Select Grade"
          optionLabel="label"
          optionValue="value"
          disabled={!selectedSyllabus}
          showClear
          onClear={() => handleClearFilter("grade.name", "gradeId")}
        />
        &nbsp;&nbsp;
        <Dropdown
          value={selectedSubject}
          options={subjectOptions}
          onChange={handleSubjectChange}
          placeholder="Select Subject"
          optionLabel="label"
          optionValue="value"
          disabled={!selectedSyllabus}
          showClear
          onClear={() => handleClearFilter("subject.name", "subjectId")}
        />
        &nbsp;&nbsp;
        <Button
          icon="pi pi-filter-slash"
          className="p-button-rounded"
          style={{
            background: "none",
            border: "none",
            borderColor: "#708db6",
            color: "#708db6",
            fontSize: "0.5rem",
          }}
          onClick={() => {
            handleClearFilter("syllabus.name", "syllabusId");
            handleClearFilter("medium.name", "mediumId");
            handleClearFilter("grade.name", "gradeId");
            handleClearFilter("subject.name", "subjectId");
          }}
        />
        <br />
        <br />
        <DataTable
          value={filteredPaperTypes}
          selectionMode="single"
          selection={selectedPaperType}
          style={{ marginBottom: "50px" }}
        >
          <Column field="syllabus.name" header="Syllabus" />
          <Column field="medium.name" header="Medium" />
          <Column field="grade.name" header="Grade" />
          <Column field="subject.name" header="Subject" />
          <Column field="paperTypeName" header="Paper Type" />
          <Column field="image" header="Image" body={renderImage} />
          <Column
            field="visibility"
            header="Visibility"
            body={renderVisibility}
          />
          <Column
            header="Actions"
            body={(rowData) => (
              <div>
                <CopyToClipboard
                  text={rowData._id}
                  onCopy={() => handleCopyId(rowData._id)}
                >
                  <Button
                    icon="pi pi-copy"
                    className="p-button-rounded"
                    style={{
                      backgroundColor: "#aaaaaa",
                      borderColor: "#aaaaaa",
                      color: "#ffffff",
                    }}
                  />
                </CopyToClipboard>
                &nbsp;&nbsp;
                <Button
                  icon="pi pi-pencil"
                  className="p-button-rounded p-button-success"
                  onClick={() => handleEdit(rowData)}
                />
                &nbsp;&nbsp;
                <Button
                  icon="pi pi-trash"
                  className="p-button-rounded p-button-danger"
                  onClick={() => handleDelete(rowData)}
                />
              </div>
            )}
          />
        </DataTable>
        <center>
          {loading && (
            <ProgressSpinner
              style={{ width: "50px", height: "50px" }}
              strokeWidth="5"
              animationDuration=".5s"
            />
          )}
        </center>
      </div>
      <Paginator
        first={first}
        rows={rows}
        totalRecords={totalRecords}
        onPageChange={(e) => {
          setFirst(e.first);
          setRows(e.rows);
          onPageChange(e);
        }}
      />
      <Toast ref={toast} />
    </>
  );
};

export default PaperTypeTable;
